import { ArrowRight, ArrowUpRight, BookOpenCheck, ClipboardList, Ruler, ScanSearch } from "lucide-react";
import React from "react";
import { Link } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import { buyerGuides } from "@/data/buyerGuides";
import { assets, siteConfig } from "@/data/site";

const guidePrinciples = [
  { icon: ScanSearch, title: "Use the actual room", copy: "Start with the building, conditions, project information, and the decisions that are still open." },
  { icon: Ruler, title: "Make the inputs clear", copy: "Organize material, layout, drawings, quantities, mounting, privacy, and accessory direction before final pricing." },
  { icon: ClipboardList, title: "Keep the scope honest", copy: "Use planning ranges and reference information to start the conversation; confirm the project-specific scope by quote." },
];

export default function BuyerGuides() {
  const url = `${siteConfig.url}/resources/buyer-guides`;
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collection`,
    url,
    name: "Commercial Bathroom Partition Buyer Guides",
    description: "Practical Buyer Guides for commercial bathroom partition materials, budget planning, quote preparation, and supplier evaluation.",
    isPartOf: { "@id": `${siteConfig.url}/#website` },
    mainEntity: { "@id": `${url}#guides` },
    breadcrumb: { "@id": `${url}#breadcrumb` },
    primaryImageOfPage: `${siteConfig.url}${assets.planningDrawing}`,
  };
  const listSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${url}#guides`,
    name: "Commercial bathroom partition buyer guides",
    numberOfItems: buyerGuides.length,
    itemListElement: buyerGuides.map((guide, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: guide.title,
      url: `${siteConfig.url}/resources/buyer-guides/${guide.slug}`,
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: "Resources", item: `${siteConfig.url}/resources` },
      { "@type": "ListItem", position: 3, name: "Buyer Guides", item: url },
    ],
  };

  return (
    <>
      <StructuredData data={[collectionSchema, listSchema, breadcrumbSchema]} />
      <header className="buyer-guides-hero section-line">
        <div className="buyer-guides-hero__copy">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span><Link href="/resources">Resources</Link><span>/</span>Buyer guides</p>
          <span className="kicker">Commercial restroom planning</span>
          <h1>Buyer guides for a clearer commercial restroom scope.</h1>
          <p>Use these practical guides to compare materials, plan an early partition budget, prepare a quote request, and evaluate the full partition and accessory scope. Each guide is designed to help a real project move forward.</p>
          <Link href="#buyer-guide-index" className="button button--primary">Browse Buyer Guides <ArrowRight aria-hidden="true" /></Link>
        </div>
        <figure className="buyer-guides-hero__visual technical-figure">
          <img src={assets.planningDrawing} srcSet={`${assets.planningDrawingSmall} 720w, ${assets.planningDrawingMedium} 1200w, ${assets.planningDrawing} 1600w`} sizes="(max-width: 820px) 100vw, 50vw" width="1600" height="873" alt="Commercial restroom planning drawing with partitions, screens, fixtures, grab bars, mirrors, and dispensers" fetchPriority="high" />
          <figcaption><span>Plan before pricing</span><strong>Materials + layout + scope</strong></figcaption>
        </figure>
      </header>

      <section id="buyer-guide-index" className="buyer-guide-index section-line" aria-labelledby="buyer-guide-index-title">
        <header className="buyer-guide-index__heading">
          <div><span className="kicker">Buyer Guide library</span><h2 id="buyer-guide-index-title">Start with the question in front of you.</h2></div>
          <p>These guides are planning tools, not final specifications, code determinations, product promises, or project quotes. The final system is confirmed for the actual job.</p>
        </header>
        <div className="buyer-guide-index__grid">
          {buyerGuides.map((guide) => (
            <Link href={`/resources/buyer-guides/${guide.slug}`} key={guide.slug}>
              <span>{guide.number}</span>
              <h3>{guide.title}</h3>
              <p>{guide.cardCopy}</p>
              <strong>Open guide <ArrowRight aria-hidden="true" /></strong>
            </Link>
          ))}
        </div>
      </section>

      <section className="buyer-guide-principles section-line" aria-labelledby="buyer-guide-principles-title">
        <div><span className="kicker kicker--light">Useful information first</span><h2 id="buyer-guide-principles-title">Better inputs create a better next question.</h2><p>Partition Stalls uses the information available to organize a project-specific conversation. Where the project is incomplete, the goal is to make the gaps visible, not to force a generic answer.</p></div>
        <div>{guidePrinciples.map(({ icon: Icon, title, copy }, index) => <article key={title}><span>0{index + 1}</span><Icon aria-hidden="true" /><h3>{title}</h3><p>{copy}</p></article>)}</div>
      </section>

      <QuoteBand title="Ready to turn the planning into a project quote?" copy="Start with the information you know, upload plans or drawings if you have them, and add any material, quantity, accessory, mounting, or privacy direction that will help frame the actual project." />
    </>
  );
}
